import { take, tap } from 'rxjs/operators';
import { Validators, FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { Component, Input, OnDestroy, OnInit, LOCALE_ID, Output, EventEmitter, ChangeDetectionStrategy, ViewChild, inject } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { NotificationService } from '../../services/notification.service';
import { ItemOutService } from '../../services/item-out.service';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from '../../services';
import { CallOffRequestResponse, ItemOutCP } from '../../models/item-out-model';
import { RequestActionService } from '../../services/request-action.service';
import { DateUtility } from '../../date.utility';
import { DateFormatType } from '../../enums/date-format-type.enum';
import { Customer } from '../../models/consumer';
import { getCustomer } from '../../store/domains/customer/customer.selectors';
import { CallOffRequest } from '../../models/calloff-request.model';
import { ReturnRequestType } from '../../enums/return-request-type.enum';
import { AppFormValidators } from '../../admin/validators/app.validators';
import { AppFacadeService } from '../../services/app-facade.service';
import { AppMediatorService } from '../../services/app-mediator.service';
import { NgxMatTimepickerComponent } from 'ngx-mat-timepicker';
import { ContractDetail } from '../../models/contract-model';
import { FormatAddressPipe } from '../../shared/pipes/format-address.pipe';
import { DialogContentType } from '../../enums/dialog-type.enum';
import { sumOfArray } from '../../utils/sumOfArray.util';
import { AuthUiService } from '../../services/auth-ui.service';

@Component({
    standalone: false,
    selector: 'por-close-item-out',
    templateUrl: './close-item-out.component.html',
    styleUrls: ['./close-item-out.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CloseItemOutComponent implements OnInit, OnDestroy {
    appFacadeService = inject(AppFacadeService);
    private readonly store = inject(Store);
    private readonly itemOutService = inject(ItemOutService);
    private readonly notification = inject(NotificationService);
    private readonly translateService = inject(TranslateService);
    private readonly logger = inject(LoggerService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly requestActionService = inject(RequestActionService);
    private readonly fb = inject(FormBuilder);
    private readonly formatAddressPipe = inject(FormatAddressPipe);
    private readonly authService = inject(AuthUiService);

    dateFormat: string;
    isStepValid = false;
    minDate = new Date(); // Set it to the current date to disable past dates.
    currentTime!: string;
    @Input() uiUrl: string | undefined;

    @Output()
    readonly isFormReturn: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Output()
    readonly callOffRequestInfo: EventEmitter<CallOffRequest> = new EventEmitter<CallOffRequest>();

    @ViewChild('timepicker') timepicker: NgxMatTimepickerComponent | undefined;

    returnForm: FormGroup;
    constructor() {
        const locale = inject(LOCALE_ID);

        this.dateFormat = DateUtility.getDateDisplayFormat(DateFormatType.StandardDate, locale);
        this.returnForm = this.fb.group({
            isSecure: ['', [Validators.required]],
            customerName: ['', [Validators.required, Validators.maxLength(255)]],
            customerPhone: ['', [Validators.required, Validators.maxLength(30)]],
            email: ['', [Validators.required, AppFormValidators.emailValidator, Validators.maxLength(255)]],
            jobSiteName: ['', [Validators.required, Validators.maxLength(255)]],
            jobSitePhone: ['', [Validators.required, Validators.maxLength(30)]],
            requestedDate: ['', [Validators.required, AppFormValidators.futureDateValidator]],
            requestedTime: ['', [Validators.required]],
            gateCombo: ['', [Validators.nullValidator, Validators.maxLength(255)]],
            address: ['', [Validators.required, Validators.maxLength(255)]],
            comments: ['', [Validators.nullValidator]]
        });
    }
    enabledTab$ = this.requestActionService.activeTab$;

    sent = true;
    subscriptions: Subscription[] = [];
    @Input() items!: ItemOutCP[];
    @Input() customerId!: string;
    @Input() contractDetail?: ContractDetail | null;
    customer$!: Observable<Customer>;
    customerInfo!: Customer | null;
    ngOnInit(): void {
        const lang = this.appMediatorService.localStorageService.selectedContentLanguage;
        if (lang && lang !== this.translateService.getCurrentLang()) {
            this.translateService.use(lang);
        }

        this.returnForm.setValue({
            isSecure: '',
            customerName: '',
            customerPhone: '',
            email: '',
            jobSiteName: '',
            jobSitePhone: '',
            requestedDate: '',
            requestedTime: '',
            gateCombo: '',
            address: this.getDeliveryAddress(),
            comments: ''
        });
        this.customer$ = this.store.pipe(select(getCustomer));

        this.subscriptions.push(
            this.customer$
                .pipe(
                    take(1),
                    tap(customer => {
                        this.customerInfo = customer;
                    })
                )
                .subscribe()
        );
        this.currentTime = this.formatTime(new Date());
        this.addDynamicControls();
    }
    ngOnDestroy(): void {
        this.subscriptions.map(sub => sub.unsubscribe());
        this.removeDynamicControls();
    }

    private addDynamicControls() {
        if (this.items && this.items.length > 0) {
            this.items.forEach((item, index) => {
                if (item?.isBulk) {
                    const calledOffQuantity = item?.callOffQty && Array.isArray(item?.callOffQty) ? sumOfArray(item.callOffQty) : 0;
                    const lineItemQty = item?.actualQuantity || item.quantity;
                    /**
                     * Substract called off quantity from actual quantity, so that it won't be over limit
                     */
                    const maxQuantity = lineItemQty - calledOffQuantity;
                    this.returnForm.addControl(`callOffQty_${index}`, this.fb.control(maxQuantity, [Validators.required, Validators.min(1), Validators.max(maxQuantity)]));
                }
            });
        }
    }

    private removeDynamicControls() {
        Object.keys(this.returnForm.controls).forEach(controlName => {
            if (controlName.startsWith('callOffQty_')) {
                this.returnForm.removeControl(controlName);
            }
        });
    }

    getFormControl(controlName: string): FormControl {
        return this.returnForm.get(controlName) as FormControl;
    }

    get isSecure() {
        return this.returnForm.get('isSecure');
    }

    get customerEmail() {
        return !this.appFacadeService.versionToggleService.isQuickLinkVersion() ? this.authService.user.Email : '';
    }

    get customerName() {
        return this.returnForm.get('customerName');
    }

    get customerPhone() {
        return this.returnForm.get('customerPhone');
    }

    get email() {
        return this.returnForm.get('email');
    }

    get jobSiteName() {
        return this.returnForm.get('jobSiteName');
    }

    get jobSitePhone() {
        return this.returnForm.get('jobSitePhone');
    }

    get requestedDate() {
        return this.returnForm.get('requestedDate');
    }

    get requestedTime() {
        return this.returnForm.get('requestedTime');
    }

    get gateCombo() {
        return this.returnForm.get('gateCombo');
    }

    get address() {
        return this.returnForm.get('address');
    }

    get comments() {
        return this.returnForm.get('comments');
    }

    checkPickupRequest() {
        if (this.isSecure?.valid && this.requestedDate?.valid && this.requestedTime?.valid) {
            return true;
        }
        return false;
    }

    checkSiteInfo() {
        if (this.address?.valid && this.gateCombo?.valid && this.jobSiteName?.valid && this.jobSitePhone?.valid) {
            return true;
        }

        return false;
    }

    sendCallOff() {
        this.appFacadeService.setLoading(true);

        if (!this.returnForm.valid) {
            this.notification.error(this.translateService.instant('pleaseFillRequired'));
            this.appFacadeService.setLoading(false);
            return false;
        }

        if (!this.customerId) {
            this.logger.logError('Customer Id Not Found in your request');
            return;
        }
        /**
         * this.items @Input(), so can't be extensible, so define new variable to assign desired call of quantity
         */
        const itemsCopy = this.items.map((item, index) => {
            const contral = this.getFormControl('callOffQty_' + index);
            /**
             * If control not found, mean item.IsBulk = false, in case return item as it is
             */
            if (contral) {
                return {
                    ...item,
                    callOffQty: contral.value
                };
            } else {
                return {
                    ...item,
                    callOffQty: item.actualQuantity
                };
            }
        });
        this.subscriptions.push(
            this.itemOutService
                .calloffrent({
                    items: itemsCopy,
                    customerId: this.customerId,
                    customerName: this.returnForm.value?.customerName,
                    accountName: this.customerInfo?.CompanyName ?? this.customerInfo?.Name,
                    ...this.returnForm.value,
                    returnType: ReturnRequestType.Return
                })
                .subscribe({
                    next: (res: CallOffRequestResponse) => {
                        this.appFacadeService.setLoading(false);
                        this.returnForm.reset();
                        this.callOffRequestInfo.emit(res.callOffRequest);
                        this.appFacadeService.openDialog(DialogContentType.RequestSentSuccessfully);
                    },
                    error: err => {
                        this.appFacadeService.setLoading(false);
                        this.logger.alertDevError(err);
                        // this.returnForm.reset();
                        this.notification.error(this.translateService.instant('somethingWentWrong'));
                    }
                })
        );
        return true;
    }

    getDeliveryAddress(): string {
        if (this.contractDetail?.DriverDelivery?.DeliveryAddress) {
            return this.formatAddressPipe.format(this.contractDetail?.DriverDelivery?.DeliveryAddress);
        }
        return '';
    }

    return() {
        this.isFormReturn.emit(true);
    }

    openTimePicker(): void {
        if (this.timepicker) {
            this.timepicker.open();
        }
    }

    formatTime(date: Date): string {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    }

    isAddressExists(): boolean {
        return !!this.returnForm.get('address')?.value && !!this.getDeliveryAddress();
    }
}
